import axios from "axios";

var SERVER_URL = "/api";

if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
  SERVER_URL = "http://localhost:5001/api";
} else {
  SERVER_URL = "/api";
}

export const serverURL = () => {
  let url = "/api";

  if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
    url = "http://localhost:5001/api";
  } else {
    url = "/api";
  }
  return url;
};

export const httpPublic = axios.create({
  baseURL: `${SERVER_URL}/public`,
  timeout: 50000,
  headers: {
    "Content-type": "application/json",
  },
});

export const httpCommonAuth = axios.create({
  baseURL: SERVER_URL,
  withCredentials: true,
  timeout: 50000,
  headers: {
    "Content-type": "application/json",
  },
});

// Injected from index.js after store creation — avoids circular import
// (store → apiSlice → http.service → store)
let _store = null;
let _actions = null;

export const injectStore = (store, actions) => {
  _store = store;
  _actions = actions;
};

// --- Refresh coordination ---
// Prevents multiple simultaneous /refresh calls when several requests fail at once.
let isRefreshing = false;
let pendingQueue = []; // { resolve, reject }[]

const flushQueue = (token, error) => {
  pendingQueue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(token)
  );
  pendingQueue = [];
};

// Always send the latest token stored in Redux on every request.
httpCommonAuth.interceptors.request.use(
  (config) => {
    if (_store) {
      const token = _store.getState().auth.token;
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// On 401/403: silently refresh, then retry the original request.
// Concurrent requests that fail while a refresh is in progress are queued
// and retried with the new token once the refresh completes.
httpCommonAuth.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if ((status === 401 || status === 403) && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return httpCommonAuth(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const { data } = await axios.get(`${SERVER_URL}/refresh`, {
          withCredentials: true,
        });
        const { accessToken } = data;

        if (_store && _actions) {
          _store.dispatch(_actions.setCredentials({ ...data }));
        }

        // Keep the default header in sync for all future requests
        httpCommonAuth.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${accessToken}`;

        flushQueue(accessToken, null);

        originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
        return httpCommonAuth(originalRequest);
      } catch (refreshError) {
        flushQueue(null, refreshError);
        if (_store && _actions) {
          _store.dispatch(_actions.logOut());
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);
